/**
 * KND-3c: the termination confirmations of one tenancy (KND-3b endpoints).
 *
 * Three calls, nothing else: read the server's state (readiness, current
 * version with its stale flag, history), generate a new version, and fetch a
 * short-lived signed URL for one version's PDF. Readiness, "current" and
 * "stale" are decided by the server; no object key, no snapshot and no hash
 * ever reach this client.
 *
 * A failed request throws an Error whose message is the server's own text and
 * whose `status` is the HTTP status, so a caller can tell a refusal (409 /
 * 422 — the server state moved) from a transport failure.
 */
import { API_BASE_URL, getApiHeaders, parseAdminErrorResponse } from "./core";

function confirmationsUrl(tenancyId) {
  return `${API_BASE_URL}/api/admin/tenancies/${encodeURIComponent(
    tenancyId
  )}/termination/confirmations`;
}

async function confirmationError(res, fallback) {
  const detail = await parseAdminErrorResponse(res);
  const err = new Error(detail || fallback);
  err.status = res.status;
  return err;
}

/** GET /api/admin/tenancies/{tenancyId}/termination/confirmations */
export async function fetchAdminTerminationConfirmations(tenancyId) {
  const res = await fetch(confirmationsUrl(tenancyId), {
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw await confirmationError(
      res,
      "Kündigungsbestätigung konnte nicht geladen werden."
    );
  }
  return res.json();
}

/**
 * POST /api/admin/tenancies/{tenancyId}/termination/confirmations
 *
 * A new version from the current data; the previous version of the same
 * termination becomes superseded. 422 when the server says it is not ready.
 */
export async function generateAdminTerminationConfirmation(tenancyId) {
  const res = await fetch(confirmationsUrl(tenancyId), {
    method: "POST",
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw await confirmationError(
      res,
      "Kündigungsbestätigung konnte nicht erstellt werden."
    );
  }
  return res.json();
}

/** GET /api/admin/termination-confirmations/{confirmationId}/pdf/url */
export async function fetchAdminTerminationConfirmationPdfUrl(confirmationId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/termination-confirmations/${encodeURIComponent(
      confirmationId
    )}/pdf/url`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) {
    throw await confirmationError(res, "PDF-Link konnte nicht geladen werden.");
  }
  return res.json();
}
